import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  BedtimeOutlined,
  Collections,
  ContactMail,
  LightMode,
  Logout,
  Mail,
  Notifications,
  NotInterested,
  People,
  RssFeed,
  SettingsBrightness,
  ShoppingBasket,
  Schedule,
} from '@mui/icons-material';

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { utilisateurPropType } from 'generic/core/qes/proptypes';
import { civiliteToGender } from 'generic/utils/qesUtils';
import UserAvatar from 'generic/components/ui/UserAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { saveProfile } from 'generic/core/config/actions';

const TooltipContent = React.memo(({ user }) => {
  const gender = civiliteToGender(user.civilite);
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <Typography sx={{ fontWeight: 'bold' }}>
        {t('account_menu.signed_in_as', { context: gender, logon: user.logon })}
      </Typography>
      {`${user.prenom} ${user.nom}`}
      <br />
      <i>{user.email}</i>
    </React.Fragment>
  );
});

TooltipContent.propTypes = {
  user: utilisateurPropType.isRequired,
};

const AccountMenu = ({
  user,
  hasActiveCollection = false,
  hasCarts = false,
  hasCollections = false,
  hasWatches = false,
  webTrackingAccess = false,
  avatarSx = {},
}) => {
  const dispatch = useDispatch();

  const handleChangeThemeApp = (event) => {
    const params = {
      utilisateur: user.utilisateur,
      theme_qes: Number(event.target.value),
    };
    dispatch(saveProfile(params, true));
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleClose = () => { setAnchorEl(null); };
  const theme = useTheme();
  const smallerThanMedium = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const { t } = useTranslation();
  const menuWithAnchorStyle = {
    overflow: 'visible',
    boxShadow: theme.palette.mode === 'dark' ? `
      0px 1px 2px 0px rgb(255 255 255 / 33%),
      0px 1px 4px 0px rgb(255 255 255 / 33%),
      0px 1px 4px 0px rgb(255 255 255 / 33%)
    ` : 5,
    mt: 1.5,
    minWidth: '15%',
    maxHeight: '100%',
    '& .MuiAvatar-root': {
      width: 32,
      height: 32,
      marginLeft: -0.5,
      marginRight: 1,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: smallerThanMedium ? 10 : 15,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
    },
  };

  return (
    <Fragment>
      <Tooltip title={<TooltipContent user={user} />}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 2, p: 0 }}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <UserAvatar user={user} sx={avatarSx} />
        </IconButton>
      </Tooltip>

      <Menu
        PaperProps={{
          sx: menuWithAnchorStyle,
        }}
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          component={Link}
          to="/profile"
          selected={location.pathname === '/profile'}
        >
          <ListItemIcon><UserAvatar user={user} sx={{ boxShadow: '0 0 3px 0px #c5c5c5' }} /></ListItemIcon>
          <ListItemText>{t('profile.account')}</ListItemText>
        </MenuItem>
        <Divider />
        <ToggleButtonGroup
          value={user.theme_qes}
          onChange={handleChangeThemeApp}
          exclusive
          color="primary"
          size="small"
          fullWidth
          sx={{
            boxSizing: 'border-box',
            px: 1,
            pb: 1,
          }}
        >
          <ToggleButton value={1} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LightMode fontSize="small" />
            {t('profile.theme.light')}
          </ToggleButton>
          <ToggleButton value={2} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BedtimeOutlined fontSize="small" />
            {t('profile.theme.dark')}
          </ToggleButton>
          <ToggleButton value={1000} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SettingsBrightness fontSize="small" />
            {t('profile.theme.system')}
          </ToggleButton>
        </ToggleButtonGroup>

        {hasCollections && (
          <Divider sx={{ mb: 1 }} />
        )}

        {hasCollections && (
          <MenuItem
            component={Link}
            to="/collections"
            selected={location.pathname.startsWith('/collections')}
          >
            <ListItemIcon><Collections fontSize="small" /></ListItemIcon>
            <ListItemText>{t('header.collections')}</ListItemText>
          </MenuItem>
        )}

        {(hasCarts || hasWatches) && (
          <Divider />
        )}

        {hasCarts && hasActiveCollection && (
          <MenuItem
            component={Link}
            to="/carts"
            selected={location.pathname.startsWith('/carts')}
          >
            <ListItemIcon><ShoppingBasket fontSize="small" /></ListItemIcon>
            <ListItemText>{t('header.carts')}</ListItemText>
          </MenuItem>
        )}

        {hasWatches && hasActiveCollection && (
          <MenuItem
            component={Link}
            to="/alerts"
            selected={location.pathname.startsWith('/alerts')}
          >
            <ListItemIcon><Notifications fontSize="small" /></ListItemIcon>
            <ListItemText>{t('header.alerts')}</ListItemText>
          </MenuItem>
        )}

        <Divider />
        <MenuItem
          component={Link}
          to="/mailingLists"
          selected={location.pathname.startsWith('/mailingLists')}
        >
          <ListItemIcon><Mail fontSize="small" /></ListItemIcon>
          <ListItemText>{t('header.mailing_lists')}</ListItemText>
        </MenuItem>
        <MenuItem
          component={Link}
          to="/users-mailing-list"
          selected={location.pathname.startsWith('/users-mailing-list')}
        >
          <ListItemIcon><ContactMail fontSize="small" /></ListItemIcon>
          <ListItemText>{t('header.users_mailing_list')}</ListItemText>
        </MenuItem>
        {user.admin && (
          <MenuItem
            component={Link}
            to="/users"
            selected={location.pathname === '/users'}
          >
            <ListItemIcon><People fontSize="small" /></ListItemIcon>
            <ListItemText>{t('users.list')}</ListItemText>
          </MenuItem>
        )}

        {webTrackingAccess && hasActiveCollection && (
          [
            <Divider key="divider_1" />,
            <MenuItem
              component={Link}
              key="accountMenu/anr/sources"
              selected={location.pathname === '/anr/sources'}
              to="/anr/sources"
            >
              <ListItemIcon><RssFeed fontSize="small" /></ListItemIcon>
              <ListItemText>{t('account_menu.sources')}</ListItemText>
            </MenuItem>,
            <MenuItem
              component={Link}
              key="accountMenu/anr/banned"
              selected={location.pathname === '/anr/banned'}
              to="/anr/banned"
            >
              <ListItemIcon><NotInterested fontSize="small" /></ListItemIcon>
              <ListItemText>{t('anr.banned_sites')}</ListItemText>
            </MenuItem>,
          ]
        )}

        {user.admin && <Divider />}
        {user.admin && (
          <MenuItem
            component={Link}
            to="/schedules"
            selected={location.pathname === '/schedules'}
          >
            <ListItemIcon><Schedule fontSize="small" /></ListItemIcon>
            <ListItemText>{t('header.schedules')}</ListItemText>
          </MenuItem>
        )}
        {
          hasActiveCollection && (
            <MenuItem
              component={Link}
              to="/monitoring"
              selected={location.pathname === '/monitoring'}
            >
              <ListItemIcon><Schedule fontSize="small" /></ListItemIcon>
              <ListItemText>{t('header.imports_monitoring')}</ListItemText>
            </MenuItem>
          )
        }

        <Divider />
        <MenuItem
          component={Link}
          to="/logout"
        >
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          <ListItemText>{t('account_menu.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Fragment>
  );
};

AccountMenu.propTypes = {
  user: utilisateurPropType.isRequired,
  hasActiveCollection: PropTypes.bool,
  hasCarts: PropTypes.bool,
  hasCollections: PropTypes.bool,
  hasWatches: PropTypes.bool,
  webTrackingAccess: PropTypes.bool,
  avatarSx: PropTypes.shape({
    sx: PropTypes.shape(),
  }),
};

export default AccountMenu;
